// PreventiApp · Sprint 3 · Etapa 7 — Página de Usuarios (admin_colegio + INSIDE).
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  listUsuarios, createUsuario, updateUsuario, resetUsuarioPassword,
} from '../../data/supabaseAdmin';

const ROLES = ['docente','inspector','orientador','encargado_convivencia','directivo','admin_colegio','super_admin_inside'];
const ESTADOS = ['activo','inactivo','pendiente_revision'];

export default function AdminUsuarios() {
  const { profile } = useAuth();
  const isInside = profile?.es_inside_staff === true;
  const colegioCtx = isInside ? null : profile?.colegio_id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [lastTempPwd, setLastTempPwd] = useState(null);
  const [filterRol, setFilterRol] = useState('');

  const reload = async () => {
    setLoading(true); setError(''); setLastTempPwd(null);
    try { setItems(await listUsuarios({ colegio_id: colegioCtx })); } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [colegioCtx]);

  const visibles = useMemo(() => filterRol ? items.filter((u) => u.rol === filterRol) : items, [items, filterRol]);

  const startCreate = () => {
    setCreating(true); setEditing(null);
    setDraft({
      colegio_id: profile?.colegio_id || '',
      rol: 'docente', nombre_completo: '', rut: '', correo: '', telefono_movil: '', cargo: '',
    });
  };
  const startEdit = (u) => {
    setCreating(false); setEditing(u.id);
    setDraft({ ...u });
  };
  const cancel = () => { setCreating(false); setEditing(null); setDraft({}); setLastTempPwd(null); };

  const save = async () => {
    try {
      if (creating) {
        const res = await createUsuario(draft);
        setLastTempPwd({ correo: draft.correo, temp_password: res.temp_password });
      } else {
        await updateUsuario(editing, {
          rol: draft.rol, nombre_completo: draft.nombre_completo,
          telefono_movil: draft.telefono_movil, cargo: draft.cargo, estado: draft.estado,
        });
      }
      setCreating(false); setEditing(null); setDraft({});
      reload();
    } catch (e) { alert(`Error: ${e.message}`); }
  };

  const onResetPassword = async (u) => {
    if (!window.confirm(`¿Resetear contraseña de ${u.correo}? Se generará una temporal y deberá cambiarla al ingresar.`)) return;
    try {
      const res = await resetUsuarioPassword(u.id);
      setLastTempPwd({ correo: u.correo, temp_password: res.temp_password });
    } catch (e) { alert(`Error: ${e.message}`); }
  };

  const downloadTempCsv = () => {
    if (!lastTempPwd) return;
    const csv = `\ufefftipo,correo,password_temporal\n${creating ? 'nuevo' : 'reset'},${lastTempPwd.correo},${lastTempPwd.temp_password}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `respaldo_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="page-usuarios">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B1B5E]">Gestión de usuarios</h1>
          <p className="text-xs text-[#475472] mt-1">
            {isInside ? 'Estás viendo los perfiles según tus permisos.' : 'Estos son los perfiles registrados en tu colegio.'}
          </p>
        </div>
        <div className="flex gap-2">
          <select value={filterRol} onChange={(e) => setFilterRol(e.target.value)} data-testid="usuarios-filter-rol"
                  className="px-3 py-2 rounded-xl border border-[#DDE6F7] text-sm">
            <option value="">Todos los roles</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={startCreate} data-testid="usuario-btn-nuevo"
                  className="px-4 py-2 rounded-xl bg-[#5346E6] hover:bg-[#0B1B5E] text-white text-sm font-bold">
            + Nuevo usuario
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm" data-testid="usuario-error">{error}</div>}

      {lastTempPwd && (
        <div className="mb-4 p-4 rounded-xl bg-[#FFF1D6] border border-[#F6B73C] text-[#7A4A0B] text-sm" data-testid="usuario-temp-pwd">
          <p className="font-bold">⚠️ Contraseña temporal generada para <span className="font-mono">{lastTempPwd.correo}</span></p>
          <p className="mt-1 font-mono text-base">{lastTempPwd.temp_password}</p>
          <p className="mt-1 text-xs">Sólo se muestra una vez. El envío automático por correo aún no está habilitado — descarga el CSV de respaldo y entrégalo de forma segura al usuario.</p>
          <div className="mt-2 flex gap-2">
            <button onClick={downloadTempCsv} data-testid="usuario-temp-pwd-download"
                    className="px-3 py-1.5 rounded-lg bg-[#0B1B5E] text-white text-xs font-bold">Descargar CSV respaldo</button>
            <button onClick={() => setLastTempPwd(null)} className="px-3 py-1.5 rounded-lg border border-[#7A4A0B] text-xs font-bold">Cerrar</button>
          </div>
        </div>
      )}

      {(creating || editing) && (
        <div className="mb-5 p-5 rounded-2xl bg-white border border-[#5346E6]/30 shadow-sm" data-testid="usuario-form">
          <p className="text-sm font-extrabold text-[#0B1B5E] mb-3">{creating ? 'Alta de usuario' : `Editar: ${draft.nombre_completo}`}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <label className="block">Nombre completo
              <input data-testid="usuario-input-nombre" value={draft.nombre_completo || ''} onChange={(e) => setDraft({ ...draft, nombre_completo: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]" />
            </label>
            <label className="block">RUT
              <input disabled={!creating} value={draft.rut || ''} onChange={(e) => setDraft({ ...draft, rut: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7] disabled:bg-[#F4F7FE]" />
            </label>
            <label className="block">Correo
              <input data-testid="usuario-input-correo" disabled={!creating} value={draft.correo || ''} onChange={(e) => setDraft({ ...draft, correo: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7] disabled:bg-[#F4F7FE]" />
            </label>
            <label className="block">Rol
              <select data-testid="usuario-input-rol" value={draft.rol || 'docente'} onChange={(e) => setDraft({ ...draft, rol: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]">
                {ROLES.filter((r) => isInside || r !== 'super_admin_inside').map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="block">Teléfono <input value={draft.telefono_movil || ''} onChange={(e) => setDraft({ ...draft, telefono_movil: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]" /></label>
            <label className="block">Cargo <input value={draft.cargo || ''} onChange={(e) => setDraft({ ...draft, cargo: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]" /></label>
            {!creating && (
              <label className="block">Estado
                <select data-testid="usuario-input-estado" value={draft.estado || 'activo'} onChange={(e) => setDraft({ ...draft, estado: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]">
                  {ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="usuario-btn-save" onClick={save} className="px-4 py-2 rounded-xl bg-[#10B981] hover:bg-[#059669] text-white text-sm font-bold">Guardar</button>
            <button onClick={cancel} className="px-4 py-2 rounded-xl border border-[#DDE6F7] text-[#475472] text-sm font-bold">Cancelar</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5EAF5] rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="usuarios-table">
          <thead className="bg-[#F4F7FE] text-[#0B1B5E] text-xs">
            <tr>
              <th className="px-4 py-3 text-left font-bold">Nombre</th>
              <th className="px-4 py-3 text-left font-bold">Correo</th>
              <th className="px-4 py-3 text-left font-bold">Rol</th>
              <th className="px-4 py-3 text-left font-bold">Estado</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {visibles.map((u) => (
              <tr key={u.id} className="border-t border-[#E5EAF5]" data-testid={`usuario-row-${u.rut?.replace(/\W/g, '')}`}>
                <td className="px-4 py-3 font-bold text-[#0B1B5E]">{u.nombre_completo}</td>
                <td className="px-4 py-3 text-xs text-[#475472]">{u.correo}</td>
                <td className="px-4 py-3 text-xs">{u.rol}{u.es_inside_staff && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-[#EEF0FE] text-[#5346E6] font-bold text-[10px]">INSIDE</span>}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${u.estado === 'activo' ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#F4F7FE] text-[#475472]'}`}>{u.estado}</span></td>
                <td className="px-4 py-3 text-right text-xs space-x-2">
                  <button onClick={() => startEdit(u)} data-testid={`usuario-edit-${u.id.slice(0,8)}`} className="font-bold text-[#5346E6] hover:underline">Editar</button>
                  <button onClick={() => onResetPassword(u)} data-testid={`usuario-reset-${u.id.slice(0,8)}`} className="font-bold text-[#B97A0D] hover:underline">Reset pwd</button>
                </td>
              </tr>
            ))}
            {!loading && visibles.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#475472]">Sin perfiles.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
